import * as React from 'react';
import { StrictMode } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { router } from 'lib/tanstack-router/router';
// REDUX
import { Provider } from 'react-redux';
import { store } from './redux/store';
// React Query
import { QueryClient, QueryCache, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
// MATERIAL UI
import { ThemeProvider, Theme, StyledEngineProvider } from '@mui/material/styles';
import theme from './styles/muiTheme';
// HELMET
import { HelmetProvider } from 'react-helmet-async';
// GOOGLE MAPS
import { GoogleMapsProvider } from 'components/common/GoogleMapsProvider';
// CONFIG
import { FlagsContext, launch_darkly_flags } from './flagsContext';
import LaunchDarklyFetch from 'fetch/LaunchDarklyFetch'; // uncomment if using flags in context
import {
  TEN_MINUTES,
  LD_MIEDGE_BROKER_SEARCH_PREMIUM_ALLOCATION_SUMMARY,
  LD_MIEDGE_BROKER_SEARCH_FILTER_CONTACT_ROLE_TITLES,
  LD_MIEDGE_BROKER_SEARCH_BROKER_SHARE,
  LD_MIEDGE_BROKER_SEARCH_FOR_ALL,
  LD_MIEDGE_BROKER_SEARCH_EXPORT_LOCATION_BOB,
  LD_MIEDGE_BROKER_CLEAR_FILTERS,
  LD_MIEDGE_BROKER_SEARCH_EXPORT_CONTACTS,
  LD_MIEDGE_UPDATE_EXPIRED_EMAIL_LINK,
  LD_MIEDGE_PREMIUM_CONTACTS_5500_SIGNER,
} from 'lib/config/appConstants';
import { TableProgressProvider } from 'context/TableProgressContext';
// MODULE STATE INITIALIZATION
import { initializeModuleState } from 'lib/moduleStateUtils';

declare module '@mui/styles/defaultTheme' {
  interface DefaultTheme extends Theme {}
}

/**
 * Component that initializes Redux module state from the current URL
 * This ensures the module state matches the URL on app startup
 */
function ModuleStateInitializer() {
  React.useEffect(() => {
    initializeModuleState();
  }, []);

  return null; // This component doesn't render anything
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({}),
  defaultOptions: {
    queries: {
      staleTime: TEN_MINUTES,
      gcTime: Infinity,
    },
  },
});

export const Providers = () => {
  const [launchDarklyFlags, setLaunchDarklyFlags] = React.useState(launch_darkly_flags);

  /*
  Launch Darkly Feature toggles

  To include a launch darkly feature toggle, follow the pattern on the commented
  out code below. Each flag is required to have it's own try/catch block. The value
  of the flag is added to the tempFlags object.

  Constants are used to hold the snake case flag name found in Lunch Darkly.

  Once all flags values are retrieved from the proxy server, State is updated with the
  final value of tempFlags which will include all flags needed and current value.

  note: this method will not update the state of the flag unless the user reloads the page/app.
*/
  React.useEffect(() => {
    const controller = new AbortController();
    const getFlag = async () => {
      let tempFlags = { ...launchDarklyFlags };
      /* SAMPLE CODE DO NOT DELETE
try {
        const ldMiedgeUpdateExpiredEmailLinkFlag = await LaunchDarklyFetch.getLaunchDarklyVariation(
          LD_MIEDGE_UPDATE_EXPIRED_EMAIL_LINK
        );
        if (!controller.signal.aborted) {
          tempFlags = {
            ...tempFlags,
            ldMiedgeUpdateExpiredEmailLink:
              // note: this is optional but enables the flag for all users 
              // in dev and test environments without needing to set it in LaunchDarkly
              process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'test'
                ? true
                : ldMiedgeUpdateExpiredEmailLinkFlag.value,
          };
        }
      } catch {
        if (!controller.signal.aborted) {
          tempFlags = {
            ...tempFlags,
            ldMiedgeUpdateExpiredEmailLink: false,
          };
        }
      }
      */

      try {
        const ldMiedgeBrokerSearchPremiumAllocationSummaryFlag =
          await LaunchDarklyFetch.getLaunchDarklyVariation(
            LD_MIEDGE_BROKER_SEARCH_PREMIUM_ALLOCATION_SUMMARY
          );
        if (!controller.signal.aborted) {
          tempFlags = {
            ...tempFlags,
            ldMiedgeBrokerSearchPremiumAllocationSummary:
              ldMiedgeBrokerSearchPremiumAllocationSummaryFlag.value,
          };
        }
      } catch {
        if (!controller.signal.aborted) {
          tempFlags = { ...tempFlags, ldMiedgeBrokerSearchPremiumAllocationSummary: false };
        }
      }

      // EDP-2250: Add broker share to location summary panels (app.newEdge)
      try {
        const ldMiedgeBrokerSearchBrokerShareFlag =
          await LaunchDarklyFetch.getLaunchDarklyVariation(LD_MIEDGE_BROKER_SEARCH_BROKER_SHARE);
        if (!controller.signal.aborted) {
          tempFlags = {
            ...tempFlags,
            ldMiedgeBrokerSearchBrokerShare: ldMiedgeBrokerSearchBrokerShareFlag.value,
          };
        }
      } catch {
        if (!controller.signal.aborted) {
          tempFlags = { ...tempFlags, ldMiedgeBrokerSearchBrokerShare: false };
        }
      }

      // EDP-2205: Broker Search - Front End Filtering for Contact Job Title/Role
      try {
        const ldMiedgeBrokerSearchFilterContactRoleTitlesFlag =
          await LaunchDarklyFetch.getLaunchDarklyVariation(
            LD_MIEDGE_BROKER_SEARCH_FILTER_CONTACT_ROLE_TITLES
          );
        if (!controller.signal.aborted) {
          tempFlags = {
            ...tempFlags,
            ldMiedgeBrokerSearchFilterContactRoleTitles:
              ldMiedgeBrokerSearchFilterContactRoleTitlesFlag.value,
          };
        }
      } catch {
        if (!controller.signal.aborted) {
          tempFlags = { ...tempFlags, ldMiedgeBrokerSearchFilterContactRoleTitles: false };
        }
      }

      // EDP-2281: Book of Business Export - Update app GUI - Show export button
      try {
        const ldMiedgeBrokerSearchExportLocationBobFlag =
          await LaunchDarklyFetch.getLaunchDarklyVariation(
            LD_MIEDGE_BROKER_SEARCH_EXPORT_LOCATION_BOB
          );
        if (!controller.signal.aborted) {
          tempFlags = {
            ...tempFlags,
            ldMiedgeBrokerSearchExportLocationBob: ldMiedgeBrokerSearchExportLocationBobFlag.value,
          };
        }
      } catch {
        if (!controller.signal.aborted) {
          tempFlags = { ...tempFlags, ldMiedgeBrokerSearchExportLocationBob: false };
        }
      }

      try {
        const ldMiedgeBrokerClearFiltersFlag = await LaunchDarklyFetch.getLaunchDarklyVariation(
          LD_MIEDGE_BROKER_CLEAR_FILTERS
        );
        if (!controller.signal.aborted) {
          tempFlags = {
            ...tempFlags,
            ldMiedgeBrokerClearFilters: ldMiedgeBrokerClearFiltersFlag.value,
          };
        }
      } catch {
        if (!controller.signal.aborted) {
          tempFlags = { ...tempFlags, ldMiedgeBrokerClearFilters: false };
        }
      }

      // EDP-2159: Broker Dashboard - Select Contacts
      try {
        const ldMiedgeBrokerSearchExportContactsFlag =
          await LaunchDarklyFetch.getLaunchDarklyVariation(LD_MIEDGE_BROKER_SEARCH_EXPORT_CONTACTS);
        if (!controller.signal.aborted) {
          tempFlags = {
            ...tempFlags,
            ldMiedgeBrokerSearchExportContacts: ldMiedgeBrokerSearchExportContactsFlag.value,
          };
        }
      } catch {
        if (!controller.signal.aborted) {
          tempFlags = { ...tempFlags, ldMiedgeBrokerSearchExportContacts: false };
        }
      }

      // EDP-2369: Update expired email link and text
      try {
        const ldMiedgeUpdateExpiredEmailLinkFlag = await LaunchDarklyFetch.getLaunchDarklyVariation(
          LD_MIEDGE_UPDATE_EXPIRED_EMAIL_LINK
        );
        if (!controller.signal.aborted) {
          tempFlags = {
            ...tempFlags,
            ldMiedgeUpdateExpiredEmailLink:
              process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'test'
                ? true
                : ldMiedgeUpdateExpiredEmailLinkFlag.value,
          };
        }
      } catch {
        if (!controller.signal.aborted) {
          tempFlags = {
            ...tempFlags,
            ldMiedgeUpdateExpiredEmailLink: false,
          };
        }
      }

      try {
        const ldMiedgePremiumContacts5500SignerFlag =
          await LaunchDarklyFetch.getLaunchDarklyVariation(LD_MIEDGE_PREMIUM_CONTACTS_5500_SIGNER);
        if (!controller.signal.aborted) {
          tempFlags = {
            ...tempFlags,
            ldMiedgePremiumContacts5500Signer: ldMiedgePremiumContacts5500SignerFlag.value,
          };
        }
      } catch {
        if (!controller.signal.aborted) {
          tempFlags = { ...tempFlags, ldMiedgePremiumContacts5500Signer: false };
        }
      }

      // Fetch LaunchDarkly completed
      if (!controller.signal.aborted) {
        tempFlags = { ...tempFlags, isFetching: false };
        setLaunchDarklyFlags(tempFlags);
      }
    };

    getFlag();
    return () => {
      controller.abort();
    };
  }, []);

  return (
    <StrictMode>
      <Provider store={store}>
        <ModuleStateInitializer />
        <QueryClientProvider client={queryClient}>
          <HelmetProvider>
            <FlagsContext.Provider value={launchDarklyFlags}>
              <TableProgressProvider>
                <StyledEngineProvider injectFirst>
                  <ThemeProvider theme={theme}>
                    <GoogleMapsProvider>
                      <RouterProvider router={router} />
                    </GoogleMapsProvider>
                  </ThemeProvider>
                </StyledEngineProvider>
              </TableProgressProvider>
            </FlagsContext.Provider>
            <ReactQueryDevtools initialIsOpen={false} /> {/* Default position='bottom' */}
          </HelmetProvider>
        </QueryClientProvider>
      </Provider>
    </StrictMode>
  );
};
